import _ from 'lodash';
import constants from '../../../../config/constants';
import { generateDeliveryText } from '../../../../utils/hooks/useCurrentDeliveryText';
import { getSelectedData } from '../DeliveryOptions.Helper';
import { convertToNumber, getLocaleTime, pad } from '../utils';

const { MONTHS, AVAILABILITY_HOMEDELIVERY, AVAILABILITY_OUTOFSTOCK, AVAILABILITY_INSTOCK } = constants;

const processHomeDeliverySlots = (
  homeDeliverySlots,
  homeDeliveryDateRangeSlots,
  regionCode,
  isFreeShippingApplicable
) => {
  const currentLocaleTime = getLocaleTime(regionCode);
  const currentDate = new Date(currentLocaleTime).getDate();
  const currentHour = new Date(currentLocaleTime).getHours();
  const currentTime = new Date(currentLocaleTime).getTime();
  const nextDate = new Date(currentTime + 24 * 3600 * 1000).getDate();
  const result = [];
  let i;
  for (i = 0; i < homeDeliverySlots.length; i += 1) {
    const slot = homeDeliverySlots[i];
    const customSlots = _.get(slot, 'customTimeSlots', []);
    if (customSlots.length === 0) {
      // eslint-disable-next-line no-continue
      continue;
    }
    const date = Number(_.get(slot, 'slotDate.date', 0));
    const month = _.get(slot, 'slotDate.month', '').toLowerCase();
    const price = _.get(slot, 'customTimeSlots[0].cost', undefined);
    if (date === currentDate && currentHour < 13 && (isFreeShippingApplicable || price !== undefined)) {
      result.push({
        title: 'Hoy',
        price: isFreeShippingApplicable ? 'Gratis' : price,
        date: pad(date),
        month: MONTHS[month],
        show: true,
        isDateRange: false,
      });
      break;
    } else if (date === currentDate && currentHour >= 13 && (isFreeShippingApplicable || price !== undefined)) {
      result.push({
        title: 'Mañana',
        price: isFreeShippingApplicable ? 'Gratis' : price,
        date: pad(date),
        month: MONTHS[month],
        show: true,
        isDateRange: false,
      });
      break;
    } else if (date === nextDate && (isFreeShippingApplicable || price !== undefined)) {
      result.push({
        title: 'Mañana',
        price: isFreeShippingApplicable ? 'Gratis' : price,
        date: pad(date),
        month: MONTHS[month],
        show: true,
        isDateRange: false,
      });
      break;
    } else if (date !== 0 && (isFreeShippingApplicable || price !== undefined) && month !== '') {
      result.push({
        title: `Desde el`,
        price: isFreeShippingApplicable ? 'Gratis' : price,
        date: pad(date),
        month: MONTHS[month],
        show: true,
        isDateRange: false,
      });
      break;
    }
  }

  if (homeDeliveryDateRangeSlots.length > 0) {
    const dateRangePrice = _.get(homeDeliveryDateRangeSlots, '[0].cost', undefined);

    const description = _.get(homeDeliveryDateRangeSlots, '[0].description', '');

    const dateRe = /[\d]+/g;
    const dateArray = description.match(dateRe) || [];
    let dateRangeDay = '';
    let dateRangeMonth = '';
    if (dateArray && Array.isArray(dateArray) && dateArray.length > 0) {
      [dateRangeDay, dateRangeMonth] = dateArray;
      dateRangeMonth = Number(dateRangeMonth);
    }

    if (isFreeShippingApplicable || dateRangePrice !== undefined) {
      result.push({
        title: 'Envío en rango de fechas',
        price: isFreeShippingApplicable ? 'Gratis' : dateRangePrice,
        date: dateRangeDay,
        month: dateRangeMonth,
        show: true,
        isDateRange: true,
      });
    }
  }

  if (homeDeliverySlots.length > 0 && isFreeShippingApplicable) {
    result.push({
      title: 'Envío en fecha programada',
      price: 'Gratis',
      date: '',
      month: '',
      show: true,
      isDateRange: false,
    });
    return result;
  }

  const regex = /[\d.,]+/g;

  const costArray = homeDeliverySlots.flatMap((slot) =>
    (slot.customTimeSlots || []).flatMap((timeSlot) => {
      const cost = timeSlot.cost || '';
      const normalisedCost = cost.match(regex);
      return normalisedCost && normalisedCost.length > 0
        ? { value: convertToNumber(normalisedCost[0]), original: cost }
        : [];
    })
  );

  if (costArray.length > 0) {
    const normalizedCosts = costArray.map((item) => item.value);

    const minCost = Math.min(...normalizedCosts);
    const maxCost = Math.max(...normalizedCosts);

    const foundMinOriginal = costArray.find((cost) => cost.value === minCost);
    const minOriginal = foundMinOriginal ? foundMinOriginal.original : costArray[0].original;

    const foundMaxOriginal = costArray.find((cost) => cost.value === maxCost);
    const maxOriginal = foundMaxOriginal ? foundMaxOriginal.original : costArray[0].original;

    const reqCost = minCost === maxCost ? minOriginal : `${minOriginal}-${maxOriginal}`;

    result.push({
      title: 'Envío en fecha programada',
      price: reqCost,
      date: '',
      month: '',
      show: true,
      isDateRange: false,
    });
  }

  return result;
};

const getHomeDeliverySlots = async ({ regionCode, homeDelivery, isFreeShippingApplicable }) => {
  const homeDeliverySlots = _.get(homeDelivery, 'data.homeDelivery.deliverySlots', []);
  const homeDeliveryDateRangeSlots = _.get(homeDelivery, 'data.homeDeliveryDateRange.deliverySlots', []);
  if (
    (!homeDeliverySlots || homeDeliverySlots.length === 0) &&
    (!homeDeliveryDateRangeSlots || homeDeliveryDateRangeSlots.length === 0)
  ) {
    return [];
  }
  const result = processHomeDeliverySlots(
    homeDeliverySlots,
    homeDeliveryDateRangeSlots,
    regionCode,
    isFreeShippingApplicable
  );
  if (result && Array.isArray(result) && result.length > 0) {
    return result;
  }
  return [];
};

const getHomeDeliveryData = async ({
  regionCode,
  comuna,
  isDefaultComuna,
  isFreeShippingApplicable,
  homeDelivery,
  setComunaName,
  setHomeDeliveryData,
  setParentStateName,
  setDeliveryError,
  setIsMapView,
  setSelectedDeliveryOption,
  labelsData,
  setTextGenerated,
}) => {
  if (comuna && !_.isEmpty(comuna)) {
    const comunaName = _.get(comuna, 'name', '');
    const parentStateName = _.get(comuna, 'parentStateName', '');
    setParentStateName(parentStateName);
    const hdResult = await getHomeDeliverySlots({
      regionCode,
      isFreeShippingApplicable,
      homeDelivery,
    });
    if (hdResult && Array.isArray(hdResult) && hdResult.length > 0) {
      setDeliveryError(AVAILABILITY_HOMEDELIVERY, false);
      setHomeDeliveryData(hdResult);
      setComunaName(comunaName);
      const { selectedType, price, date, month, storeName, storeId, isValid, locationName, politicalId, isDateRange } =
        getSelectedData(hdResult, AVAILABILITY_HOMEDELIVERY, comuna, isDefaultComuna);
      if (isValid) {
        const selectionData = {
          type: selectedType,
          price,
          date,
          month,
          locationName,
          storeName,
          storeId,
          politicalId,
          isError: false,
          isDateRange,
          isFreeShippingApplicable,
          parentStateName,
          deliveryOptionType: AVAILABILITY_HOMEDELIVERY,
        };
        const stockText = generateDeliveryText(
          selectionData,
          AVAILABILITY_INSTOCK,
          labelsData,
          AVAILABILITY_HOMEDELIVERY
        );
        setTextGenerated((prev) => ({ ...prev, [AVAILABILITY_HOMEDELIVERY]: stockText }));
        setSelectedDeliveryOption(selectionData);
      }
    } else {
      setComunaName(comunaName);
      setDeliveryError(AVAILABILITY_HOMEDELIVERY, true, 'NO_DELIVERY_SLOTS');
      setIsMapView(false);
      const selectionData = {
        selectedType: 'No disponible para',
        date: '',
        month: '',
        price: '',
        locationName: comunaName.toLowerCase(),
        storeName: '',
        storeId: '',
        isError: true,
        isFreeShippingApplicable: false,
        parentStateName,
        deliveryOptionType: AVAILABILITY_HOMEDELIVERY,
      };
      const stockText = generateDeliveryText(
        selectionData,
        AVAILABILITY_OUTOFSTOCK,
        labelsData,
        AVAILABILITY_HOMEDELIVERY
      );
      setTextGenerated((prev) => ({ ...prev, [AVAILABILITY_HOMEDELIVERY]: stockText }));
      setSelectedDeliveryOption(selectionData);
    }
  }
};

export { getHomeDeliveryData, getHomeDeliverySlots, processHomeDeliverySlots };
